import React from "react";
import { Users, Wifi, Plug, Sparkles, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Star } from "@/components/Monogram";

const ICONS = [Users, Wifi, Plug, Sparkles, ShieldCheck];

export default function Vehicle() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const v = content && content.vehicle;
  if (!v) return null;
  const features = v.features[lang] || v.features.it;
  const payments = (lang === "en" ? v.payments_en : v.payments) || v.payments;
  const marquee = [...payments, ...payments];

  return (
    <section id="viaggiare" className="relative pt-24 pb-14 sm:pt-32 sm:pb-16 lg:pt-40" data-testid="vehicle-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>{t(v.kicker)}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display mt-6 text-3xl font-light leading-tight text-[#FAF9F6] sm:text-4xl lg:text-5xl" data-testid="vehicle-title">
                {t(v.title)}
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-8 text-sm font-light leading-relaxed text-[#A1A1AA] sm:text-base">{t(v.text)}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8 border-l border-[#C5A059]/50 pl-5">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059]">{lang === "en" ? "Please note" : "Attenzione"}</p>
                <p className="mt-2 text-[13px] font-light leading-relaxed text-[#FAF9F6]/60">{t(v.note)}</p>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <h3 className="font-display mt-12 text-xl font-light text-[#FAF9F6]">{t(v.features_title)}</h3>
              <ul className="mt-6 space-y-0 border-t hairline" data-testid="vehicle-features">
                {features.map((f, i) => {
                  const Icon = ICONS[i % ICONS.length];
                  return (
                    <li key={i} className="flex items-center gap-4 border-b hairline py-4">
                      <Icon size={16} strokeWidth={1.25} className="text-[#C5A059]" aria-hidden="true" />
                      <span className="text-[15px] font-light text-[#FAF9F6]/85">{f}</span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <Reveal delay={0.1} className="col-span-2">
                <div className="group relative overflow-hidden">
                  <img
                    src={v.images[0]}
                    alt="Obsidian Private Driver sulle strade delle Dolomiti"
                    className="img-spotlight aspect-[16/10] w-full object-cover"
                    loading="lazy"
                    data-testid="vehicle-image-main"
                  />
                  <div className="pointer-events-none absolute inset-0 border hairline" />
                </div>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="group relative overflow-hidden">
                  <img
                    src={v.images[1]}
                    alt="Il conducente di Obsidian"
                    className="img-spotlight aspect-[3/4] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 border hairline" />
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="group relative overflow-hidden">
                  <img
                    src={v.images[2]}
                    alt="Mercedes Classe V di Obsidian"
                    className="img-spotlight aspect-[3/4] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 border hairline" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-3" data-testid="vehicle-badges">
          {[
            { icon: <span className="font-display text-[34px] font-light text-[#FAF9F6]">6</span>, label: { it: "Posti", en: "Seats" } },
            { icon: <Wifi size={38} strokeWidth={1.1} className="text-[#FAF9F6]" />, label: { it: "Wi-Fi gratuito", en: "Free Wi-Fi" } },
            { icon: <Plug size={38} strokeWidth={1.1} className="text-[#FAF9F6]" />, label: { it: "Ricarica dispositivi", en: "Device recharge" } },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#FAF9F6]/50">
                  {b.icon}
                </div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#FAF9F6]/75">{b.label[lang]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-24 border-y hairline py-10 marquee-paused" data-testid="payments-marquee">
          <p className="eyebrow mb-8 text-center !text-[9px]">{t(v.payments_title)}</p>
          <div className="relative flex overflow-hidden">
            <div className="animate-marquee flex shrink-0 items-center gap-10 pr-10" style={{ "--marquee-duration": "55s" }}>
              {marquee.map((p, i) => (
                <span key={i} className="flex items-center gap-10 whitespace-nowrap" aria-hidden={i >= payments.length}>
                  <span className="text-[13px] font-light uppercase tracking-[0.25em] text-[#FAF9F6]/70" data-testid={i < payments.length ? `payment-${i}` : undefined}>
                    {p}
                  </span>
                  <Star size={7} fill="#C5A059" className="opacity-70" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
